/**
 * updateCell.ts - Update a single cell in the table.
 *
 * Exposes the public {@link updateCell} method (orchestration: analytics + persistence) and the pure
 * {@link _updateCellTransform} it wraps.
 */

import produce from 'immer';

import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import type {Cell} from 'notebook/tables/constants/CellConstants';
import {EVALUABLE_CELL_PROPERTIES} from 'notebook/tables/constants/CellConstants';
import {type Table} from 'notebook/tables/constants/TableConstants';
import {type TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type TableMutationResult, type TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import {
  computeWorksheetInfo,
  persistTable,
  type TableMutationParams,
} from 'notebook/tables/store/logic/mutations/mutationParams';
import TableUtil from 'notebook/tables/TableUtil';
import {sanitizeTypedValueForSave} from 'notebook/tables/typed-values/DataTypes';
import {trackCrossStepReferenceChange} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';
import {omit} from 'util/omitKey';

/**
 * Update a single cell in the table, then track analytics and persist the change.
 *
 * @param params - The shared mutation dependencies.
 * @param cellIndex - The [row, column] index of the cell to update.
 * @param cellData - Partial cell data to merge into the existing cell.
 * @param expandTable - If true, add a new blank row at the bottom.
 */
export function updateCell(
  params: TableMutationParams,
  cellIndex: TableIndex,
  cellData: Partial<Cell>,
  expandTable?: boolean
): void {
  const model = params.getTableModel();
  const {table, fileId, tableNamespace} = params.getTableProps();
  const {boundaryIndex: singleInputBoundaryIndex} = model.singleInputs;

  // Execute pure mutation
  const result = _updateCellTransform(table, cellIndex, cellData, {
    expandTable,
    singleInputBoundaryIndex,
  });

  // Track cross-step reference analytics
  if (cellData.text != null) {
    const oldCellText = result.affectedCells[0]?.oldCell?.text ?? '';
    trackCrossStepReferenceChange(oldCellText, cellData.text, computeWorksheetInfo(tableNamespace, fileId));
  }

  // Persist the change
  persistTable(params, result);

  // Track container validity analytics
  const newCell = result.affectedCells[0]?.newCell;
  if (newCell) {
    trackContainerValidityForTableEvents(fileId, table.apiId!, [
      {event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE, cells: [newCell]},
    ]);
  }
}

export interface UpdateCellOptions {
  /** If true, add a new blank row at the bottom of the table */
  expandTable?: boolean;
  /** If present and expandTable is true, remerge single input columns */
  singleInputBoundaryIndex?: number | null;
}

/**
 * Update a single cell in the table.
 *
 * @param table - The current table
 * @param index - The [row, column] index of the cell to update
 * @param cellData - Partial cell data to merge into the existing cell
 * @param options - Optional configuration for table expansion
 * @returns The mutation result with new table, operation, and affected cells
 */
export function _updateCellTransform(
  table: Table,
  index: TableIndex,
  cellData: Partial<Cell>,
  options: UpdateCellOptions = {}
): TableMutationResult {
  const [i, j] = index;
  const oldCell = table.table[i]?.[j];

  // Strip merge properties - these should only be modified through mergeRegion/unmergeRegion TableOps
  cellData = omit(cellData, 'mergeRowSpan', 'mergeColSpan');

  // Check if any properties that trigger evaluation have changed
  let requiresEvaluation = false;
  for (const property of Object.keys(cellData)) {
    const propertyKey = property as keyof Cell;
    if (
      [...EVALUABLE_CELL_PROPERTIES].includes(propertyKey) &&
      cellData[propertyKey] !== oldCell[propertyKey]
    ) {
      requiresEvaluation = true;
      break;
    }
  }

  // Perform the cell update inline (replaces updateCellInTable)
  // Any usage of cell.error should be computed and automatically reapplied by the store if still applicable
  const baseCell = omit(oldCell, 'error', 'typedValue');
  const newCell: Cell = {...baseCell, ...cellData};
  if (cellData.typedValue != null) {
    newCell.typedValue = sanitizeTypedValueForSave(cellData.typedValue);
  }

  let updatedTable = produce(table, (draftTable) => {
    draftTable.table[i][j] = newCell;
  });
  let tableOp: TableOpDiff | null = requiresEvaluation
    ? {type: 'update-cells', cells: [{row: i, column: j}]}
    : null;

  // If 'expand' is true, add a new blank row at the bottom of the table
  if (options.expandTable) {
    const newRowIndex = updatedTable.table.length;
    updatedTable = produce(updatedTable, (draft) => {
      draft.table = [...draft.table, ...TableUtil.makeBlankRows(draft, 1)];
    });
    requiresEvaluation = true;
    // override the tableOp to use upsert-cells since we're adding a new row
    tableOp = {
      type: 'upsert-cells',
      cells: [{row: i, column: j}],
      newRowIndexes: [newRowIndex],
    };

    // If any single inputs are present and the table is expanding, merge the single input columns
    if (options.singleInputBoundaryIndex) {
      updatedTable = produce(updatedTable, (draft) => {
        draft.table = TableUtil.remergeSingleInputColumns(draft).table;
      });
    }
  }

  return {
    table: updatedTable,
    tableOp,
    affectedCells: [{row: i, column: j, oldCell, newCell}],
  };
}
